import './style.css';
import { Sprite } from 'pixi.js';
import { AtlasBuilder, detectTier, Engine, QUALITY_PRESETS, ambientForHour, screenToTile, } from '@enugu/engine';
import { parseAreaMap } from '@enugu/shared';
import { characterArtItems, ProceduralDollFrames, randomOutfit } from './art/charArt';
import { PAL, rng } from './art/paint';
import { worldArtItems } from './art/worldArt';
import { buildCoalCamp, MAP_H, MAP_W } from './scene/coalCamp';
import { Wanderers } from './npc/wanderers';
import { mountUi } from './ui/UiRoot';
import { createBridge } from './ui/bridge';
import { ChatState } from './net/chat';
import { NetSession, resolveServerUrl } from './net/session';
import coalCampJson from '../../../content/maps/coal_camp.v0.json';
// ------------------------------------------------------------ URL parameters
const params = new URLSearchParams(location.search);
const num = (k, d) => {
    const v = params.get(k);
    const n = v === null ? NaN : Number(v);
    return Number.isFinite(n) ? n : d;
};
const NPCS = Math.max(0, Math.min(2000, Math.floor(num('npcs', 50))));
const DEBUG = params.get('debug') === '1';
const SEED = num('seed', 7);
const START_HOUR = num('time', 17.5);
const BENCH_SEC = num('bench', 0);
const qualityParam = params.get('quality');
const wallsParam = params.get('walls');
/** Device signals for tier detection (all optional in some browsers). */
function gatherDeviceInfo() {
    let gpu;
    try {
        const c = document.createElement('canvas');
        const gl = c.getContext('webgl2') ?? c.getContext('webgl');
        if (gl) {
            const ext = gl.getExtension('WEBGL_debug_renderer_info');
            gpu = String(gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
            gl.getExtension('WEBGL_lose_context')?.loseContext();
        }
    }
    catch {
        /* no WebGL info */
    }
    const nav = navigator;
    const info = {
        dpr: window.devicePixelRatio || 1,
        cores: navigator.hardwareConcurrency || 4,
        screenW: screen.width,
        screenH: screen.height,
        isMobile: /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent) || matchMedia('(pointer: coarse)').matches,
    };
    if (nav.deviceMemory !== undefined)
        info.memoryGB = nav.deviceMemory;
    if (gpu)
        info.gpu = gpu;
    return info;
}
async function main() {
    const app = document.getElementById('app');
    const loading = document.getElementById('loading');
    const device = gatherDeviceInfo();
    const decision = qualityParam && qualityParam in QUALITY_PRESETS ? { tier: qualityParam, reasons: ['?quality= override'] } : detectTier(device);
    const quality = QUALITY_PRESETS[decision.tier];
    console.info(`[enugu] quality ${decision.tier}: ${decision.reasons.join(', ')}`, device);
    const engine = await Engine.create({
        parent: app,
        quality,
        background: PAL.laterite,
        debug: DEBUG,
        adaptiveResolution: params.get('adaptive') !== '0',
        preference: params.get('renderer') === 'webgpu' ? 'webgpu' : 'webgl',
    });
    // ---------------------------------------------------------------- assets
    const bake = (items, label) => () => {
        const b = new AtlasBuilder(engine.renderer, { artScale: quality.artScale, label });
        for (const it of items)
            b.add(it);
        return b.build();
    };
    engine.assets.registerGenerator('coal-camp-world', bake(worldArtItems(), 'world'));
    engine.assets.registerGenerator('characters', bake(characterArtItems(), 'chars'));
    const manifest = {
        area: 'coal-camp',
        version: 'm0',
        entries: [
            { key: 'world', kind: 'procedural', generator: 'coal-camp-world', priority: 0 },
            { key: 'chars', kind: 'procedural', generator: 'characters', priority: 0 },
        ],
    };
    const tLoad = performance.now();
    await engine.assets.loadArea(manifest);
    const loadMs = performance.now() - tLoad;
    // ---------------------------------------------------------------- scene
    const dollFrames = new ProceduralDollFrames(engine.assets);
    const camp = buildCoalCamp(engine, dollFrames, SEED, parseAreaMap(coalCampJson));
    const scene = camp.scene;
    if (wallsParam === 'up' || wallsParam === 'down' || wallsParam === 'cutaway')
        scene.setWallMode(wallsParam);
    engine.setScene(scene);
    engine.camera.bounds = { left: -MAP_H * 32 - 64, right: MAP_W * 32 + 64, top: -160, bottom: (MAP_W + MAP_H) * 16 + 64 };
    const player = scene.addCharacter({
        x: camp.playerSpawn.x,
        y: camp.playerSpawn.y,
        name: 'you',
        outfit: { layers: { shadow: 'default', body: 'skin2', shoes: 'slippers', bottom: 'trousers', top: 'isiagu', hair: 'lowcut' }, tints: { bottom: 0x1f2a44 } },
    });
    const r = rng(SEED * 31 + 5);
    const wanderers = new Wanderers(scene, r);
    wanderers.spawn(NPCS);
    engine.onFixedUpdate((dt) => wanderers.update(dt));
    const startX = (player.rx - player.ry) * 32;
    const startY = (player.rx + player.ry) * 16;
    engine.camera.centerOn(startX, startY - 40);
    // Tap marker (floor decal that fades out).
    const marker = new Sprite(engine.assets.texture('decal/marker'));
    marker.alpha = 0;
    marker.tint = 0xfff1c0;
    scene.floor.decals.addChild(marker);
    let markerT = 0;
    const showMarker = (x, y) => {
        marker.position.set((x - y) * 32, (x + y + 2) * 16);
        marker.alpha = 1;
        markerT = 1;
    };
    // ---------------------------------------------------------------- input
    const tmp = { x: 0, y: 0 };
    const tile = { x: 0, y: 0 };
    let lastTile = { x: camp.playerSpawn.x, y: camp.playerSpawn.y };
    /** Nearest free tile next to a footprint, closest to the player. */
    const besideFootprint = (x, y, w, h) => {
        let best = null;
        let bd = Infinity;
        for (let j = y - 1; j <= y + h; j++) {
            for (let i = x - 1; i <= x + w; i++) {
                if (i >= x && i < x + w && j >= y && j < y + h)
                    continue;
                if (!scene.grid.isWalkable(i, j))
                    continue;
                const d = (i - player.mover.tileX) ** 2 + (j - player.mover.tileY) ** 2;
                if (d < bd) {
                    bd = d;
                    best = { x: i, y: j };
                }
            }
        }
        return best;
    };
    engine.handlers.onTap = (sx, sy) => {
        engine.audio.unlock();
        engine.screenToWorld(sx, sy, tmp);
        const hit = scene.world.pick(tmp.x, tmp.y, (it) => it.kind !== 'wall' && it.owner !== player);
        let goal = null;
        if (hit?.kind === 'prop') {
            const d = hit.owner.def;
            goal = besideFootprint(d.x, d.y, d.w, d.h);
        }
        else if (hit?.kind === 'character') {
            const c = hit.owner;
            goal = besideFootprint(c.mover.tileX, c.mover.tileY, 1, 1);
        }
        if (!goal) {
            screenToTile(tmp.x, tmp.y, 0, tile);
            goal = scene.grid.nearestWalkable(Math.floor(tile.x), Math.floor(tile.y), 6);
        }
        if (!goal)
            return;
        lastTile = goal;
        if (!net.moveTo(goal))
            scene.moveTo(player, goal.x, goal.y, true);
        showMarker(goal.x, goal.y);
        engine.camera.following = true;
    };
    engine.handlers.onPan = (dx, dy) => engine.camera.panByScreen(dx, dy);
    engine.handlers.onZoom = (f, x, y) => engine.camera.zoomAt(f, x, y);
    engine.handlers.onHover = (sx, sy) => {
        engine.screenToWorld(sx, sy, tmp);
        screenToTile(tmp.x, tmp.y, 0, tile);
        lastTile = { x: Math.floor(tile.x), y: Math.floor(tile.y) };
    };
    // ------------------------------------------------- world state & toggles
    let hour = START_HOUR;
    let timeScale = 60; // 1 real second = 1 game minute
    let nepaLight = true;
    let generatorOn = false;
    const modes = ['cutaway', 'up', 'down'];
    const applyPower = () => {
        const lampsOn = nepaLight || generatorOn;
        for (const l of camp.gridLamps) {
            l.enabled = lampsOn;
            l.def.flicker = !nepaLight && generatorOn ? 0.12 : 0;
        }
        for (const l of camp.streetLamps)
            l.enabled = nepaLight;
    };
    let hum = null;
    const applyGeneratorSound = () => {
        hum?.stop();
        hum = null;
        if (!generatorOn || !engine.audio.ready)
            return;
        // A positional "I-better-pass-my-neighbour" hum: sawtooth through a low-pass.
        const h = engine.audio.attachNode('generator', camp.generatorPos.x, camp.generatorPos.y, { refDistance: 2, maxDistance: 16, rolloff: 1.4 }, (ctx, input) => {
            const osc = ctx.createOscillator();
            osc.type = 'sawtooth';
            osc.frequency.value = 52;
            const lp = ctx.createBiquadFilter();
            lp.type = 'lowpass';
            lp.frequency.value = 260;
            const g = ctx.createGain();
            g.gain.value = 0.08;
            osc.connect(lp).connect(g).connect(input);
            osc.start();
            return () => osc.stop();
        });
        if (h)
            hum = { stop: () => engine.audio.detach('generator') };
    };
    // Dynamic obstacle test: drop/remove a bucket on a tile; walkers re-path around it.
    const buckets = new Map();
    const toggleBucket = (x, y) => {
        const k = `${x},${y}`;
        const s = buckets.get(k);
        if (s) {
            scene.grid.blockFootprint(x, y, 1, 1, false, true);
            s.destroy();
            buckets.delete(k);
            return;
        }
        if (!scene.grid.isWalkable(x, y) || (player.mover.tileX === x && player.mover.tileY === y))
            return;
        scene.grid.blockFootprint(x, y, 1, 1, true, true);
        const spr = new Sprite(engine.assets.texture('decal/oil'));
        spr.tint = 0x3f86c6;
        spr.position.set((x - y) * 32, (x + y + 2) * 16);
        scene.floor.decals.addChild(spr);
        buckets.set(k, spr);
    };
    const bridge = createBridge();
    // ---------------------------------------------------------------- net
    let hud = null;
    const chat = new ChatState(bridge);
    const serverUrl = resolveServerUrl(location, params.get('server'));
    const netLabel = { connecting: 'Connecting', online: 'Online', reconnecting: 'Network wahala', offline: 'Offline' };
    const net = new NetSession({
        scene,
        player,
        bridge,
        chat,
        outfitFor: (eid) => randomOutfit(rng(eid * 7919 + 1)),
        onStatus: () => hud?.refresh(),
    });
    bridge.me.value = { eid: null, name: 'you', online: false, net: 'offline' };
    bridge.onCommand((cmd) => net.handleCommand(cmd));
    hud = mountUi(app, bridge, [
        { id: 'net', label: () => netLabel[net.status] ?? net.status, onClick: () => { if (net.status === 'offline') net.connect(serverUrl); } },
        { id: 'walls', label: () => `Walls: ${scene.wallMode}`, onClick: () => scene.setWallMode(modes[(modes.indexOf(scene.wallMode) + 1) % modes.length]) },
        { id: 'light', label: () => (nepaLight ? 'NEPA: light dey' : 'NEPA: light don go'), onClick: () => { nepaLight = !nepaLight; applyPower(); } },
        { id: 'gen', label: () => `Gen: ${generatorOn ? 'ON' : 'off'}`, onClick: () => { engine.audio.unlock(); generatorOn = !generatorOn; applyPower(); applyGeneratorSound(); } },
        { id: 'time', label: () => `Time x${timeScale}`, onClick: () => { timeScale = timeScale === 60 ? 600 : timeScale === 600 ? 0 : 60; } },
        { id: 'follow', label: () => 'Follow', onClick: () => { engine.camera.following = true; } },
        { id: 'debug', label: () => 'Stats', onClick: () => engine.perf.toggle() },
    ]);
    applyPower();
    window.addEventListener('keydown', (e) => {
        const cam = engine.camera;
        const step = 48;
        switch (e.key) {
            case 'ArrowLeft':
            case 'a':
                cam.panByScreen(step, 0);
                break;
            case 'ArrowRight':
            case 'd':
                cam.panByScreen(-step, 0);
                break;
            case 'ArrowUp':
            case 'w':
                cam.panByScreen(0, step);
                break;
            case 'ArrowDown':
            case 's':
                cam.panByScreen(0, -step);
                break;
            case '+':
            case '=':
                cam.zoomAt(1.15, cam.viewW / 2, cam.viewH / 2);
                break;
            case '-':
                cam.zoomAt(1 / 1.15, cam.viewW / 2, cam.viewH / 2);
                break;
            case 'c':
                scene.setWallMode(modes[(modes.indexOf(scene.wallMode) + 1) % modes.length]);
                break;
            case 'l':
                nepaLight = !nepaLight;
                applyPower();
                break;
            case 'g':
                engine.audio.unlock();
                generatorOn = !generatorOn;
                applyPower();
                applyGeneratorSound();
                break;
            case 't':
                timeScale = timeScale === 60 ? 600 : timeScale === 600 ? 0 : 60;
                break;
            case 'f':
                cam.following = true;
                break;
            case 'b':
                toggleBucket(lastTile.x, lastTile.y);
                break;
            case '`':
            case 'F3':
                engine.perf.toggle();
                break;
            default: return;
        }
        hud.refresh();
    });
    // ---------------------------------------------------------------- per frame
    let clockAcc = 0;
    engine.onFrame((dt) => {
        hour = (hour + (dt * timeScale) / 3600) % 24;
        engine.lighting.setAmbient(ambientForHour(hour));
        engine.camera.follow((player.rx - player.ry) * 32, (player.rx + player.ry) * 16 - 40, dt);
        engine.audio.setListener(player.rx, player.ry);
        if (markerT > 0) {
            markerT = Math.max(0, markerT - dt * 1.2);
            marker.alpha = markerT;
        }
        chat.update((wx, wy, out) => engine.camera.worldToScreen(wx, wy, out));
        clockAcc += dt;
        if (clockAcc > 0.5) {
            clockAcc = 0;
            bridge.clock.value = { day: 1, hour };
        }
    });
    // ------------------------------------------------- profiling hooks
    const bench = { samples: [], done: BENCH_SEC <= 0, loadMs, tier: decision.tier };
    if (BENCH_SEC > 0) {
        const t0 = performance.now();
        const id = setInterval(() => {
            bench.samples.push({ ...engine.stats });
            if (performance.now() - t0 > BENCH_SEC * 1000) {
                bench.done = true;
                clearInterval(id);
            }
        }, 1000);
    }
    window.__enugu = { engine, scene, player, bench, device, decision, net };
    loading?.remove();
    engine.start();
    if (params.get('server') !== 'off')
        void net.connect(serverUrl);
}
main().catch((err) => {
    console.error(err);
    const el = document.getElementById('loading');
    if (el)
        el.textContent = `Failed to start: ${err instanceof Error ? err.message : String(err)}`;
});
